import React from 'react';
import { twMerge } from 'tailwind-merge';
import { ReactComponent as CheckMarkIcon } from '../../../assets/shared/ic-shared-success.svg';
import { Text } from '../../tokens';

export enum LiliCheckboxNewType {
  REGULAR,
  DISABLED,
  ERROR,
  ATTENTION,
}

export enum LiliCheckboxNewSize {
  SMALL,
  MEDIUM,
  LARGE,
}

export interface LiliCheckboxNewProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'type' | 'id'
> {
  id: string;
  containerClassName?: string;
  type?: LiliCheckboxNewType;
  size?: LiliCheckboxNewSize;
  checked: boolean;
  onCheckedChange: (isChecked: boolean) => void;
  onBlur?: () => void;
  label?: string | React.ReactNode;
  // TODO: Check if we can type these to only accept tailwind classes
  labelClassName?: string;
  labelTextColor?: string;
  labelAlign?: 'start' | 'center';
}

export const LiliCheckboxNew = ({
  id,
  containerClassName,
  type = LiliCheckboxNewType.REGULAR,
  size = LiliCheckboxNewSize.SMALL,
  checked = false,
  onCheckedChange,
  onBlur,
  label,
  labelClassName,
  labelTextColor,
  labelAlign = 'center',
  ...otherProps
}: LiliCheckboxNewProps) => {
  const handleCheckChange = (e: any) => {
    onCheckedChange(e.target.checked);
  };

  const getPredefinedIconSize = () => {
    switch (size) {
      case LiliCheckboxNewSize.SMALL:
        return 'h-[16px] w-[16px]';
      case LiliCheckboxNewSize.MEDIUM:
        return 'h-[16px] w-[16px]';
      case LiliCheckboxNewSize.LARGE:
        return 'h-[20px] w-[20px]';
      default:
        return '';
    }
  };

  const getPredefinedSizes = () => {
    switch (size) {
      case LiliCheckboxNewSize.SMALL:
        return 'h-[16px] w-[16px] min-w-[16px]';
      case LiliCheckboxNewSize.MEDIUM:
        return 'h-[20px] w-[20px] min-w-[20px]';
      case LiliCheckboxNewSize.LARGE:
        return 'h-[24px] w-[24px] min-w-[24px]';
      default:
        return '';
    }
  };

  const getClassNameByType = () => {
    const baseClasses = 'flex relative rounded outline-none';
    const hasBorder = !(checked && size === LiliCheckboxNewSize.SMALL);
    let typeClasses = hasBorder ? 'border ' : '';

    switch (type) {
      case LiliCheckboxNewType.REGULAR:
        typeClasses += 'bg-surface-primary-bw-00 border-outline-primary-bw-06';
        break;
      case LiliCheckboxNewType.DISABLED:
        typeClasses += 'bg-surface-primary-bw-02 border-outline-primary-bw-03 cursor-not-allowed';
        break;
      case LiliCheckboxNewType.ERROR:
        typeClasses += 'bg-surface-negative-tertiary border-outline-negative-primary';
        break;
      case LiliCheckboxNewType.ATTENTION:
        typeClasses +=
          'bg-surface-primary-bw-00 border-outline-primary-bw-06 shadow-checkboxShadow shadow-outline-brand-secondary';
        break;
      default:
        typeClasses += 'border-outline-primary-bw-06';
    }

    if (checked) {
      typeClasses += ' bg-surface-primary-bw-07 border-outline-primary-bw-07';
    } else if (type !== LiliCheckboxNewType.DISABLED) {
      typeClasses += ' hover:border-outline-primary-bw-07 hover:bg-surface-primary-bw-02';
    }

    return twMerge(baseClasses, typeClasses, getPredefinedSizes());
  };

  const checkboxElement = (
    <div
      className={twMerge(
        getClassNameByType(),
        'items-center justify-center',
        containerClassName,
        labelAlign === 'start' && 'mt-[3px]'
      )}>
      {checked && (
        <CheckMarkIcon
          className={twMerge('text-text-main-primary-inverse absolute', getPredefinedIconSize())}
        />
      )}
      <input
        type='checkbox'
        id={id}
        onChange={handleCheckChange}
        checked={checked}
        className='appearance-none z-20 inset-0 absolute'
        disabled={type === LiliCheckboxNewType.DISABLED}
        onBlur={onBlur}
      />
    </div>
  );

  if (!label) {
    return checkboxElement;
  }

  const renderLabel = () => {
    if (typeof label === 'string') {
      return <Text.H10 className={labelTextColor}>{label}</Text.H10>;
    }
    return label;
  };

  return (
    <div
      className={twMerge('flex', labelAlign === 'start' ? 'items-start' : 'items-center', 'gap-2')}
      {...otherProps}>
      {checkboxElement}
      <label htmlFor={id} className={twMerge('cursor-pointer', labelClassName)}>
        {renderLabel()}
      </label>
    </div>
  );
};
